import React, { useMemo } from 'react';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import Loader from 'src/components/Loader';
import Checkbox from 'src/components/Checkbox';
import CategoryPill from 'src/components/CategoryPill/CategoryPill';
import { IWalkthroughModel } from 'src/services/client-types';
import './UnitSelectionCard.scss';

interface UnitSelectionCardProps {
  suite: UnitBulkOperationDto;
  isSelected: boolean;
  isLoading: boolean;
  walkthroughs: IWalkthroughModel[];
  onToggle: (
    unitId: string,
    suite: UnitBulkOperationDto,
    isChecked: boolean
  ) => void;
  pendingWalkthroughCount: number;
  isFetchingWalkthroughs: boolean;
  sendSchedulingEmail: boolean
}

const UnitSelectionCard = ({
  suite,
  isSelected,
  isLoading,
  walkthroughs,
  onToggle,
  pendingWalkthroughCount,
  isFetchingWalkthroughs,
  sendSchedulingEmail
}: UnitSelectionCardProps) => {

  const isDisabled: boolean =
    (sendSchedulingEmail && !suite.hasPrimaryContact) ||
    pendingWalkthroughCount === 0 ||
    isFetchingWalkthroughs ||
    suite.hasAllActivatedWalkthroughs;

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;
    onToggle(suite.id, suite, e.target.checked);
  };

  const handleContentClick = () => {
    if (isDisabled) return;
    onToggle(suite.id, suite, !isSelected);
  };

  if (isLoading) {
    return (
      <div className="unit-card unit-card--loading">
        <div className="unit-card-loader-wrapper">
          <Loader imageHeight="28px" imageWidth="28px" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`unit-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
    >
      <Checkbox
        id={`unit-${suite.id}`}
        checked={isSelected}
        value={isSelected}
        onChange={handleCheckboxChange}
        disabled={isDisabled}
        containerStyle={{ marginRight: '12px' }}
      />
      <div className="unit-card-content" onClick={handleContentClick}>
        <div className="unit-info-stack">
          <div className="unit-card-number">{suite.number}</div>
          <div className="unit-card-building">{suite.building || '—'}</div>
          <div className="unit-card-status">{suite.status}</div>
        </div>

        <div className="unit-pills-stack">
          {/* Pill 1: No Primary Contact. */}
          {!suite.hasPrimaryContact && (
            <div className="unit-pill-wrapper">
              <CategoryPill
                text="No Primary Contact"
                color="#dc3545"
                textColor="white"
                size="tiny"
              />
            </div>
          )}
          {/* Pill 2: No Pending Walkthroughs. */}
          {pendingWalkthroughCount === 0 && !suite.hasAllActivatedWalkthroughs && (
            <div className="unit-pill-wrapper">
              <CategoryPill
                text="No Pending Walkthroughs"
                color="#6c757d"
                textColor="white"
                size="tiny"
              />
            </div>
          )}
          {/* Pill 3: All Activated. */}
          {suite.hasAllActivatedWalkthroughs && (
            <div className="unit-pill-wrapper">
              <CategoryPill
                text="All Walkthroughs already activated"
                color="#6f42c1"
                textColor="white"
                size="tiny"
              />
            </div>
          )}
          {/* Pill 4: Pending Count (Replaces the old div). */}
          {isSelected && !isDisabled && (
            <div className="unit-pill-wrapper">
              <CategoryPill
                text={`${walkthroughs?.length || 0} pending`}
                color="#e3f2fd"
                textColor="#1976d2"
                size="tiny"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnitSelectionCard;
